import type { CompanySearchResult } from '@dash-lite/sdk';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { CompanyPreview } from '@/components/company/company-preview';
import { Page } from '@/components/layout/page';
import { Checkout } from '@/components/payment/checkout';
import { Alert, Container, Section } from '@/components/ui/primitives';
import { useSession } from '@/hooks/use-session';
import { useSite } from '@/hooks/use-site';
import { paths } from '@/lib/paths';

/**
 * Page de paiement — la deuxième et dernière étape du parcours.
 *
 * Le visiteur y arrive **directement depuis la recherche** : il a choisi une
 * entreprise, il voit son aperçu public à gauche et saisit sa carte à droite.
 * Aucune page intermédiaire ne s'intercale, et aucune donnée payante n'est
 * demandée avant le paiement — l'aperçu tient entièrement dans ce que
 * `GET /companies/search` renvoie gratuitement.
 *
 * La page assemble aussi le **contexte d'achat** transmis à `POST /payment`.
 *
 * Ce contexte (`metadata`) mérite qu'on s'y arrête : l'API est sans état, elle
 * ne sait pas ce que votre client était en train de faire. Le champ permet de
 * le lui dire, et de le retrouver ensuite sur le droit d'accès — sans tenir sa
 * propre table de correspondance. Ici, on y met l'entreprise que le visiteur
 * cherchait à consulter, ce qui permet de le renvoyer au bon endroit après
 * l'achat.
 *
 * Limites imposées par Stripe, à qui le contexte est transmis : 10 clés au
 * plus, valeurs de 200 caractères. Et une règle non négociable : ce contexte
 * est visible dans le back-office et dans le compte Stripe du site — **jamais
 * de données personnelles ou confidentielles**.
 */
export default function CheckoutPage() {
    const site = useSite();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { hasAccess } = useSession();

    /** L'entreprise que le visiteur voulait consulter, si elle est connue. */
    const registrationNumber = searchParams.get('entreprise');

    /**
     * Aperçu transporté depuis la liste de résultats : il évite un second
     * appel à la recherche et affiche l'entreprise sans le moindre délai.
     */
    const preview = (location.state as { preview?: CompanySearchResult } | null)?.preview ?? null;

    // 🔴 AJOUTEZ CE CONSOLE.LOG ICI POUR LE TEST :
    console.log("=== [DEBUG] État de la page de paiement ===", {
        hasAccess,
        registrationNumber,
        locationState: location.state,
        preview,
        siteOffer: site?.offer
    });


    // Un visiteur qui a déjà un accès actif n'a rien à acheter : on l'envoie
    // directement à ce qu'il était venu consulter plutôt que de créer un
    // paiement inutile.
    if (hasAccess) {
            return <Navigate to={registrationNumber ? paths.company(registrationNumber) : paths.account} replace />;
        }

        

    if (!site.offer) {
        return (
            <Page title="Paiement">
                <Container className="py-20">
                    <Alert tone="caution" title="Aucune offre n'est configurée">
                        Ce site n'a pas encore défini son offre : il n'y a rien à acheter pour le moment.
                    </Alert>
                </Container>
            </Page>
        );
    }

    const metadata: Record<string, string> = {
        origin: 'site-exemple-docinfopro',
        ...(registrationNumber ? { registration_number: registrationNumber } : {}),
    };

    return (
        <Page title={preview?.name ?? 'Finaliser votre accès'}>


            <Section className="py-0 pb-16 bg-transparent md:bg-cream">
                <Container className="space-y-8">
                    <div className="grid items-start gap-8 lg:grid-cols-[1fr_1.05fr]">
                        <CompanyPreview registrationNumber={registrationNumber} initial={preview} />

                        <Checkout metadata={metadata} />
                    </div>
                </Container>
            </Section>
        </Page>
    );
}
